import { AccessContext, ForeignKeyField } from "lego-gui-lib";
import { NumberField, Required, Schema, TextField } from "lego-gui-lib/lib";
import { api, mainStore } from "../../core/mainStore";
import { CustomerFK } from "../customer/schema";
import { t } from "../../core/i18n/i18n";
import { SANFK } from "../san/schema";
import { RemoteChildDataSource } from "lego-gui-lib/lib/dataAccess/RemoteChildDataSource";
import {
  queryParamDtoStrategy,
  queryParamParentChangeStrategy
} from "lego-gui-lib/lib/dataAccess/ChildDataSourceStrategies";
import { QueueConfigurationFKNotRequired } from "../queueConfig/schema";
import { Auth } from "../../core/model/auth";
import { FormState } from "final-form";
import { serviceProfileAllows } from "../serviceProfile/validator";
import { BooleanField } from "../../common/common";

const onQueueActiveChange = (
  newValue: any,
  oldValue: any,
  formState: FormState<any>
) => {
  if (newValue == 1) {
    formState.values["maxCalls"] = 0;
  } else {
    formState.values["queueConfigRef"] = null;
  }
};

export const DestinationSchema = new Schema({
  primaryKey: ["id"],
  security: {
    readableRole: Auth.Roles.ANYONE,
    editableRole: Auth.Roles.ANYONE_WITH_WRITE
  },
  groups: {
    "common:main": t("common:main"),
    "hns:destination:callLimiting": t("hns:destination:callLimiting"),
    "hns:destination:callQueueing": t("hns:destination:callQueueing"),
    "common:advanced": t("common:advanced")
  },
  fields: [
    {
      name: "customerRef",
      type: CustomerFK,
      sort: { field: "name" },
      title: t("hns:destination:type.customerRef")
    },
    {
      name: "sanRef",
      type: SANFK,
      sort: { field: "name" },
      title: t("hns:destination:type.sanRef")
    },
    {
      name: "name",
      type: TextField(Required),
      title: t("common:name"),
      group: "common:main"
    },
    {
      name: "number",
      /*TODO changed this type on NumberField when better solution will be implemented*/
      type: TextField(Required),
      title: t("common:number"),
      group: "common:main"
    },
    {
      name: "discardSanExtension",
      type: BooleanField(Required),
      title: t("hns:destination:discardSanExtension"),
      group: "common:main"
    },
    {
      name: "queueActive",
      type: BooleanField(Required),
      title: t("hns:destination:queueActive"),
      group: "hns:destination:callQueueing",
      onChange: onQueueActiveChange
    },
    {
      name: "queueConfigRef",
      type: QueueConfigurationFKNotRequired,
      title: t("hns:destination:queueConfigRef"),
      group: "hns:destination:callQueueing"
    },
    {
      name: "maxCalls",
      type: NumberField(Required),
      title: t("hns:destination:maxCalls"),
      group: "hns:destination:callLimiting"
    },
    {
      name: "tgrpPrefix",
      type: TextField(),
      title: t("hns:destination:tgrpPrefix"),
      group: "common:main",
      if: (ctx: AccessContext) => mainStore.auth.isAdmin()
    },
    {
      name: "noAnswerTimeout",
      type: NumberField(),
      title: t("hns:destination:noAnswerTimeout"),
      group: "common:advanced",
      if: (ctx: AccessContext) => mainStore.auth.isAdmin()
    }
  ]
});

export const DestinationSchemaForm = (san: any) =>
  DestinationSchema.extend({
    queueConfigRef: {
      if: (ctx: AccessContext) =>
        ctx.data?.queueActive === 1 &&
        serviceProfileAllows({ data: san.oneDataSource.one }, "featQueue")
    },
    maxCalls: {
      if: () =>
        serviceProfileAllows(
          { data: san.oneDataSource.one },
          "featMaxCallsCPartyDest"
        )
    },
    queueActive: {
      if: () =>
        serviceProfileAllows({ data: san.oneDataSource.one }, "featQueue")
    },
    customerRef: false,
    sanRef: false
  });

export const DestinationSchemaColumns = (san: any) =>
  DestinationSchema.extend({
    queueConfigRef: {
      if: () =>
        serviceProfileAllows({ data: san.oneDataSource.one }, "featQueue")
    },
    maxCalls: {
      if: () =>
        serviceProfileAllows(
          { data: san.oneDataSource.one },
          "featMaxCallsCPartyDest"
        )
    },
    queueActive: {
      if: () =>
        serviceProfileAllows({ data: san.oneDataSource.one }, "featQueue")
    },
    customerRef: false,
    sanRef: false
  });

mainStore.destination = RemoteChildDataSource.create({
  parent: mainStore.SAN.oneDataSource,
  parentChangedStrategy: queryParamParentChangeStrategy("id", "sanRef"),
  dtoStrategy: queryParamDtoStrategy("id", "sanRef"),
  api: api,
  name: "destination",
  schema: DestinationSchema,
  url: "/destinations",
  commons: mainStore.commons
});

mainStore.destinationFk = RemoteChildDataSource.clone(mainStore.destination);

export const DestinationFK = ForeignKeyField(
  {
    dataSource: mainStore.destinationFk.listDataSource,
    labelField: "name",
    valueField: "id",
    minWidth: 70
  },
  Required
);
